/** DIY preview-targeting payload: only send filters the user actually chose. */

export function diyAccountIdForWire(account, tenantId) {
  const candidates = [account?.account_id, account?.id, account?.accountId]
    .map((v) => (v == null || v === '' ? null : String(v).trim()))
    .filter(Boolean);
  if (!candidates.length) return null;
  const tid = tenantId ? String(tenantId).trim() : '';
  if (tid) {
    const prefixed = candidates.find((c) => c === tid || c.startsWith(`${tid}_`));
    if (prefixed) return prefixed;
  }
  if (account?.account_id) return String(account.account_id).trim();
  return candidates[0];
}

export function fitScoreBounds(accounts) {
  const scores = (accounts || [])
    .map((a) => Number(a.fitScore ?? a.account_fit_score ?? a.accountFitScore))
    .filter((n) => Number.isFinite(n));
  if (!scores.length) return null;
  return { min: Math.min(...scores), max: Math.max(...scores) };
}

export function buildDiyPreviewPayload(formValues, { tenantId, userId } = {}) {
  const body = {
    tenant_id: tenantId,
    countryMode: formValues.countryMode,
    countries: formValues.countries,
    sectorMode: formValues.sectorMode,
    sectors: formValues.sectors,
    accountSelectionMode: formValues.accountSelectionMode,
    personaAudienceMode: formValues.personaAudienceMode,
    selectedPersonaIds: formValues.selectedPersonaIds,
    includeAllArchetypes: formValues.includeAllArchetypes,
    archetypes: formValues.archetypes,
    includeAllDepartments: formValues.includeAllDepartments,
    departments: formValues.departments,
    roleTitleKeywords: formValues.roleTitleKeywords,
    useSignals: formValues.useSignals,
  };
  if (userId) body.user_id = userId;

  if (formValues.accountSelectionMode === 'specific_accounts') {
    body.selectedAccountIds = Array.isArray(formValues.selectedAccountIds)
      ? formValues.selectedAccountIds
      : [];
  }

  if (formValues.accountSelectionMode === 'by_fit_score') {
    const n = Number(formValues.minAccountFitScore);
    body.minAccountFitScore = Number.isFinite(n) ? n : 0;
  }

  return body;
}

export function previewEmptyReason({
  accountSelectionMode,
  minAccountFitScore,
  personaAudienceMode,
  selectedPersonaIds,
  accountsCount,
  fitBounds,
}) {
  if (personaAudienceMode === 'manual' && !(selectedPersonaIds || []).length) {
    return 'Select at least one persona to preview this audience.';
  }
  if (accountSelectionMode === 'by_fit_score' && Number(accountsCount) === 0 && fitBounds) {
    const min = Number(minAccountFitScore);
    if (Number.isFinite(min) && min > fitBounds.max) {
      return `No accounts meet a fit score of ${min}. Scores in this market range from ${fitBounds.min} to ${fitBounds.max}.`;
    }
  }
  return null;
}

export const DIY_ACCOUNTS_LIST_CAP = 200;

/**
 * The DIY accounts picker is server-capped at 200. Surface that instead of
 * implying the tenant only has this many accounts.
 */
export function diyAccountsListTruncation(payload, accountsLength, cap = DIY_ACCOUNTS_LIST_CAP) {
  const returned = Number(accountsLength) || 0;
  const totalRaw = Number(payload?.total ?? payload?.total_count);
  const hasTotal = Number.isFinite(totalRaw) && totalRaw > 0;
  const truncated =
    payload?.truncated === true || (returned >= cap && (!hasTotal || totalRaw > returned));
  return {
    returned,
    total: hasTotal ? totalRaw : returned,
    truncated,
  };
}
